import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { session } from './session';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';

/**
 * Central axios client for talking to zafroi-mvp-runtime.
 *
 * Auth: sends the user's JWT access token as `Authorization: Bearer <jwt>`.
 * On 401, attempts ONE `/auth/refresh` call. If that succeeds, retries the
 * original request with the new token. If refresh fails, clears the local
 * session and emits an `auth-required` event so AuthProvider can flip to
 * unauthenticated and AuthGuard sends the user to /login.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${BASE_URL.replace(/\/$/, '')}/api/v1`,
  timeout: 20_000,
  // withCredentials so the browser sends the cross-subdomain refresh
  // cookie set on .zafroi.com by /auth/login (Phase 2 SSO). Required for
  // cookie-only refresh paths to work.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const active = session.current();
  if (active?.accessToken) {
    config.headers.set('Authorization', `Bearer ${active.accessToken}`);
  }
  return config;
});

export interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: unknown;
  error?: { code?: string; message?: string; details?: unknown };
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  constructor(
    message: string,
    {
      status,
      code,
      details,
    }: { status: number; code?: string; details?: unknown },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Single-flight refresh so concurrent 401s don't fire 5 refresh calls.
let inflightRefresh: Promise<void> | null = null;

async function performRefresh(): Promise<void> {
  // Send the localStorage refresh token if we have one; otherwise rely on
  // the cross-subdomain cookie (Phase 2 SSO). withCredentials must be set
  // so the cookie is included on this cross-origin POST.
  const refreshToken = session.getRefreshToken();
  const res = await axios.post<{
    success: boolean;
    data?: { accessToken: string; refreshToken: string; expiresAt: string };
  }>(
    `${BASE_URL.replace(/\/$/, '')}/api/v1/auth/refresh`,
    refreshToken ? { refreshToken } : {},
    {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    },
  );
  const body = res.data;
  if (!body || body.success === false || !body.data) {
    throw new Error('Refresh failed');
  }
  session.updateAccessToken(
    body.data.accessToken,
    body.data.refreshToken,
    body.data.expiresAt,
  );
}

/**
 * The runtime wraps every success in `{ success, data, meta }` (its global
 * ResponseInterceptor). Unwrap to `data` here so callers see plain payloads.
 */
apiClient.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (
      body &&
      typeof body === 'object' &&
      'success' in body &&
      'data' in body
    ) {
      response.data = (body as { data: unknown }).data;
    }
    return response;
  },
  async (error: AxiosError<ApiErrorBody>) => {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    const config = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const url = config?.url ?? '';
    // Never retry auth endpoints — refreshing on a 401 from /login would
    // recurse and the user would be misled about why login failed.
    const isAuthPublic = /\/auth\/(login|refresh|forgot-password|reset-password|mfa\/verify)/.test(url);
    const canRetry = !!config && !config._retried && !isAuthPublic;

    // Phase 2 SSO: we can no longer gate the retry on `hasRefreshToken`
    // because the refresh token may live ONLY in an HttpOnly cookie that
    // JS can't see. So we always attempt one refresh on 401 and let the
    // server decide whether a session exists. Cost: one extra POST on
    // 401s with no session at all — acceptable for the SSO win.
    if (status === 401 && canRetry) {
      try {
        if (!inflightRefresh) {
          inflightRefresh = performRefresh().finally(() => {
            inflightRefresh = null;
          });
        }
        await inflightRefresh;
        config!._retried = true;
        return apiClient(config!);
      } catch {
        session.clear();
        // AuthProvider subscribes to session changes via session.subscribe,
        // so clearing here flips the app to unauthenticated and the
        // AuthGuard redirects to /login on the next render.
      }
    }

    const message =
      body?.error?.message ??
      body?.message ??
      error.message ??
      'Request failed';
    return Promise.reject(
      new ApiError(message, {
        status,
        code: body?.error?.code ?? body?.code,
        details: body?.error?.details ?? body?.details,
      }),
    );
  },
);
